import { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import lang from '../../lib/language';
import { isEmpty } from '../../lib/util/typeGuards';

import useIsMobile from '../hooks/useIsMobile';
import NoDataAvailable from '../NoDataAvailable/NoDataAvailable';

import { StockColumn } from './components/StockColumn';
import { DividendPayerRow } from './LongTermInvestments.types';

import styles from './DividendPyersAndHighGrowthCompanies.module.scss';

const MOBILE_DIVIDENDS_COUNT_LIMIT = 6;
const HOVER_CLEAR_DELAY_MS = 140;
const PERCENT_MULTIPLIER = 100;

type Props = {
  dividendPayerContent: DividendPayerRow[];
};

const DividendPayers = ({ dividendPayerContent }: Props) => {
  const { isMobile } = useIsMobile();
  const [hoveredSymbol, setHoveredSymbol] = useState('');
  const chartPipesRef = useRef<HTMLDivElement>(null);
  const hoverClearTimeoutRef = useRef<number | null>(null);

  const sortedDividendPayers = useMemo(() => (
    [...dividendPayerContent].sort((a, b) => b.dividendYield - a.dividendYield)
  ), [dividendPayerContent]);

  const maxDividendYield = sortedDividendPayers[0]?.dividendYield ?? 0;

  const dividendsToRender = isMobile
    ? sortedDividendPayers.slice(0, MOBILE_DIVIDENDS_COUNT_LIMIT)
    : sortedDividendPayers;

  const clearHoverTimeout = () => {
    if (hoverClearTimeoutRef.current === null) {
      return;
    }

    window.clearTimeout(hoverClearTimeoutRef.current);
    hoverClearTimeoutRef.current = null;
  };

  const handleHoverElement = (symbol: string) => {
    clearHoverTimeout();

    if (symbol) {
      setHoveredSymbol(symbol);
      return;
    }

    if (isMobile) {
      setHoveredSymbol('');
      return;
    }

    hoverClearTimeoutRef.current = window.setTimeout(() => {
      setHoveredSymbol('');
      hoverClearTimeoutRef.current = null;
    }, HOVER_CLEAR_DELAY_MS);
  };

  useEffect(() => () => {
    clearHoverTimeout();
  }, []);

  if (isEmpty(dividendPayerContent)) {
    return (
      <div className={styles.wrapper}>
        <NoDataAvailable
          className={styles.noDataAvailable}
          isNoDataIcon
          title={lang.commonDiscover52WeekNoDataAll()}
        />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <p className={styles.dotWrapper}>
        <span className={classNames(styles.activeDot, styles.greenDot)} />
        {lang.commonDiscoverDividendPayersLegend() + ' (%)'}
      </p>
      <div className={styles.chartPipes} ref={chartPipesRef}>
        {dividendsToRender.map(({
          dividendYield, symbol, logoUrl, currencySymbol, dividendRate, marketCap, close, volume,
        }, index) => {
          const tooltip = {
            primaryMetric: dividendRate,
            secondaryMetric: close,
            volume,
            marketCap,
          };

          return (
            <StockColumn
              key={symbol}
              blur={hoveredSymbol !== symbol && hoveredSymbol !== ''}
              percentageLabel={String(Math.round(dividendYield * PERCENT_MULTIPLIER))}
              value={dividendYield}
              maxValue={maxDividendYield}
              stockLogo={logoUrl}
              symbol={symbol}
              isDividend
              tooltip={tooltip}
              currencySymbol={currencySymbol}
              hovered={hoveredSymbol !== '' && symbol === hoveredSymbol}
              hoverElement={handleHoverElement}
              containerRef={chartPipesRef}
              animationOrder={index}
            />
          );
        })}
      </div>
    </div>
  );
};

export default DividendPayers;
